import { useMemo } from 'react';
import { FormattedDate, IntlShape, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { GridColDef } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import { Flex } from '@atoms/layout';
import { ShiftStatusChip } from '@atoms/dataDisplay';
import { AvatarList, isDateSeparator } from '@molecules/dataDisplay';
import { ShiftList } from '@libs/models/shifts/shift';
import { Stack } from '@mui/material';
import { ShiftType } from '@pages/schedule/components';

export function useShiftListColumns() {
  const intl = useIntl();

  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<ShiftList>[] = [
      getStartDateColumn(intl),
      getShiftTypeColumn(intl),
      getQualificationsColumn(intl),
      getShareColumn(intl),
      getStatusColumn(intl),
    ];
    return columns;
  }, [intl]);
}

function getShiftTypeColumn(intl: IntlShape): GridColDef<ShiftList> {
  return {
    field: 'shiftStart',
    headerName: intl.formatMessage({
      id: 'shifts.fields.shiftType',
    }),
    flex: 1,
    minWidth: 160,
    colSpan: (_, row) => {
      if (isDateSeparator(row)) {
        return 8;
      }
      return 1;
    },
    renderCell: (params) => {
      if (isDateSeparator(params.row)) {
        return (
          <Flex alignCenter height="100%" px={0.75}>
            <Typography variant="subtitle1">
              <FormattedDate value={params.row.date} dateStyle="full" />
            </Typography>
          </Flex>
        );
      }

      return (
        <ShiftType
          startTime={dayjs(params.row.startDate).tz(params.row.timeZone)}
          endTime={dayjs(params.row.endDate).tz(params.row.timeZone)}
          boxProps={{ height: '100%' }}
          timeDisplay="full"
          isArchive={params.row.isArchive}
          isUrgent={params.row.isUrgent}
          // isShared={params.row.isShared}
          isSeries={params.row.recurrence === 'series'}
          isRotation={params.row.recurrence === 'rotation'}
        />
      );
    },
  };
}

function getStartDateColumn(intl: IntlShape): GridColDef<ShiftList> {
  return {
    field: 'none',
    headerName: intl.formatMessage({
      id: 'shifts.fields.date',
    }),
    flex: 1,
    minWidth: 80,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row;
      const formattedStart = dayjs(row.startDate).format('MM.DD.YYYY');
      const weekday = dayjs(row.startDate).format('dddd');
      return (
        <Stack direction={'column'} justifyContent={'center'} height={'100%'}>
          <Typography variant="body2">{formattedStart}</Typography>
          <Typography variant="caption">{weekday}</Typography>
        </Stack>
      );
    },
  };
}

function getQualificationsColumn(intl: IntlShape): GridColDef<ShiftList, ShiftList['nurseQualifications']> {
  return {
    field: 'nurseQualifications',
    headerName: intl.formatMessage({
      id: 'shifts.fields.qualifications',
    }),
    width: 140,
    sortComparator: (v1, v2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1[0].abbreviation}`.localeCompare(`${v2[0].abbreviation}`);
    },
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return params.value.map((qual) => (
        <Tooltip key={qual.id} title={qual.name || ''}>
          <Chip variant="filled" label={qual.abbreviation} size="small" sx={{ mr: 1 }} />
        </Tooltip>
      ));
    },
  };
}

function getShareColumn(intl: IntlShape): GridColDef<ShiftList, ShiftList['agenciesForShare']> {
  return {
    field: 'agenciesForShare',
    headerName: intl.formatMessage({ id: 'shifts.fields.agenciesForShare' }),
    width: 50,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <AvatarList
            list={params.value.map((v) => ({
              id: v.id,
              avatar: v.logoURL,
              name: v.name,
            }))}
          />
        </Stack>
      );
    },
  };
}

function getStatusColumn(intl: IntlShape): GridColDef<ShiftList> {
  return {
    field: 'status',
    headerName: intl.formatMessage({ id: 'shifts.fields.status' }),
    flex: 1,
    minWidth: 80,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return <ShiftStatusChip value={params.value} />;
    },
  };
}
